import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_GENERAL_UPDATE_LOCATIONS_TABLE } from '../../../../../../../webcomponents.constants';
import { GzPolicyUiSettingsTableWebComponent } from '../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/dataTable';
import { getTableColumns } from './grid-config';
import { html, TemplateResult } from 'lit';
import { ValidationResult } from '../../../../../../../ui/gzForm/formField/types/form-validator';
import { validateDuplicates } from './utils/validators';
import { GRID_ITEM_FORM_ID } from '../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/base/component/GzPolicyUiSettingsBaseGrid';
import { GeneralUpdateLocation, GeneralUpdateLocationPlaceholder } from '@nirvana/shared/dtos/types/policies/common';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzButtonWebComponent } from '../../../../../../../ui/gzButton';
import { toggleVisibility } from '../../../../../../../utils/htmlElement.utils';
import { GeneralUpdateLocationFormValue } from '../../../../utils/uiSettings/general/general.types';

@customElement(WEB_COMPONENT_POLICY_PANEL_GENERAL_UPDATE_LOCATIONS_TABLE)
export class GzGeneralUpdateLocationsTableWebComponent extends GzPolicyUiSettingsTableWebComponent<
  GeneralUpdateLocationFormValue,
  GeneralUpdateLocationFormValue
> {
  @query('#add-relay-server-button')
  private addRelayServerButton: GzButtonWebComponent;
  @query('#add-local-server-button')
  private addLocalServerButton: GzButtonWebComponent;

  // bind this for access to edited row index
  protected columns = getTableColumns();

  public setFormValue(value: GeneralUpdateLocation[]) {
    super.setFormValue(
      value.map(item => {
        // show translated value for default relay server
        if (item.server === GeneralUpdateLocationPlaceholder.RELAY) {
          return {
            ...item,
            server: translateFn('Relay Servers'),
            dbServerValue: GeneralUpdateLocationPlaceholder.RELAY,
          };
        }

        // show translated value for default local update server
        if (item.server === GeneralUpdateLocationPlaceholder.ARRAKIS) {
          return {
            ...item,
            server: translateFn('Local Update Server'),
            dbServerValue: GeneralUpdateLocationPlaceholder.ARRAKIS,
          };
        }

        return item;
      })
    );
  }

  public getFormValue(): GeneralUpdateLocation[] {
    const formValue = super.getFormValue();

    if (!formValue) {
      return formValue;
    }

    // map back to db values if needed
    return formValue.map((item: GeneralUpdateLocationFormValue) => {
      if (item.dbServerValue) {
        return {
          server: item.dbServerValue,
          useProxy: item.useProxy,
        };
      }

      return item;
    });
  }

  protected renderGridItemForm(): TemplateResult {
    return html`
      <gz-policy-panel-general-update-location-item
        id="${GRID_ITEM_FORM_ID}"
      ></gz-policy-panel-general-update-location-item>

      <gz-button
        id="add-relay-server-button"
        class="hidden"
        type="quiet"
        color="primary"
        theme="light"
        buttonfontsize="small"
        buttonheight="small"
        lowercase
        style="padding-top: 4px; padding-right: 24px;"
        @click="${() =>
          this.addGridItem({
            server: translateFn('Relay Servers'),
            dbServerValue: GeneralUpdateLocationPlaceholder.RELAY,
            useProxy: false,
          })}"
        >${translateFn('Add Relay Servers')}</gz-button
      >

      <gz-button
        id="add-local-server-button"
        class="hidden"
        type="quiet"
        color="primary"
        theme="light"
        buttonfontsize="small"
        buttonheight="small"
        lowercase
        style="padding-top: 4px;"
        @click="${() =>
          this.addGridItem({
            server: translateFn('Local Update Server'),
            dbServerValue: GeneralUpdateLocationPlaceholder.ARRAKIS,
            useProxy: false,
          })}"
        >${translateFn('Add Local Update Server')}</gz-button
      >
    `;
  }

  protected onEditRowEnabled(event: CustomEvent) {
    super.onEditRowEnabled(event);
    const editedItem = event.detail.item;
    const isDisabled = [GeneralUpdateLocationPlaceholder.ARRAKIS, GeneralUpdateLocationPlaceholder.RELAY].includes(
      editedItem.dbServerValue
    );

    const editedFields = this.gridComponent.shadowRoot?.querySelectorAll('.edit-form-field');
    if (editedFields) {
      editedFields.forEach((field: GzFormFieldWebComponent) => {
        field.disabled = isDisabled;
      });
    }
  }

  protected validateGridItemBeforeAdd(gridItemFormValue: GeneralUpdateLocation): ValidationResult {
    // check for duplicates
    return validateDuplicates(gridItemFormValue, this.gridComponent.getFormValue());
  }

  protected validateGridItemBeforeEdit(
    oldItem: GeneralUpdateLocation,
    newItem: GeneralUpdateLocation
  ): ValidationResult {
    // skip if item is not changed
    if (newItem.useProxy === oldItem.useProxy && newItem.server === oldItem.server) {
      return { valid: true };
    }

    return this.validateGridItemBeforeAdd(newItem);
  }

  protected async onGridItemsChange() {
    await this.gridComponent.updateComplete;

    const servers = this.gridComponent
      .getFormValue()
      .filter(item => !!item.dbServerValue)
      .map(item => item.dbServerValue);

    // show/hide add relay server button based on existing servers
    toggleVisibility(this.addRelayServerButton, !servers.includes(GeneralUpdateLocationPlaceholder.RELAY));

    // show/hide add local server button based on existing servers
    if (!Application.Main.product.isCloud()) {
      toggleVisibility(this.addLocalServerButton, !servers.includes(GeneralUpdateLocationPlaceholder.ARRAKIS));
    }
  }
}
